import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getUsers, createUser, updateUser, deleteUser, getRoles } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { Spinner } from '../components/ui/Spinner'
import { PageHeader } from '../components/ui/PageHeader'
import { ConfirmModal, useConfirm } from '../components/ui/ConfirmModal'

const createSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  roleName: z.string().min(1, 'Select a role'),
})

const ROLE_COLORS = { Admin: 'purple', Editor: 'blue', Viewer: 'gray', Manager: 'indigo' }

export default function Users() {
  const [createModal, setCreateModal] = useState(false)
  const [roleModal, setRoleModal] = useState(null)
  const [selectedRole, setSelectedRole] = useState('')
  const { confirm, close, state: confirmState } = useConfirm()
  const qc = useQueryClient()

  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers })
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: getRoles })

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { roleName: 'Admin' },
  })

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: (result) => {
      qc.setQueryData(['users'], old => [...(old ?? []), result])
      toast.success('User created'); setCreateModal(false); reset()
    },
    onError: (e) => toast.error(e.message),
  })
  const roleMut = useMutation({
    mutationFn: ({ id, roleName }) => updateUser(id, { roleName }),
    onSuccess: (result) => {
      qc.setQueryData(['users'], old => (old ?? []).map(u => u.id === result.id ? result : u))
      toast.success('Role updated'); setRoleModal(null)
    },
    onError: (e) => toast.error(e.message),
  })
  const deleteMut = useMutation({
    mutationFn: deleteUser,
    onSuccess: (_, deletedId) => {
      qc.setQueryData(['users'], old => (old ?? []).filter(u => u.id !== deletedId))
      toast.success('User deleted')
    },
    onError: (e) => toast.error(e.message),
  })

  function openRoleModal(user) { setSelectedRole(user.roleName); setRoleModal(user) }

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage admin panel users"
        action={
          <Button onClick={() => { reset({ roleName: roles?.[0]?.name ?? 'Admin' }); setCreateModal(true) }}>
            <i className="ti ti-user-plus" /> Add User
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">User</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Created</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users?.map(u => (
                <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-indigo-700">
                          {u.email.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900">{u.email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <button onClick={() => openRoleModal(u)} className="group flex items-center gap-1.5">
                      <Badge color={ROLE_COLORS[u.roleName] ?? 'gray'}>{u.roleName}</Badge>
                      <i className="ti ti-pencil text-xs text-gray-300 group-hover:text-indigo-500 transition-colors" />
                    </button>
                  </td>
                  <td className="px-5 py-4 text-gray-400 text-xs">
                    {new Date(u.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end">
                      <Button variant="ghost" size="xs" className="text-gray-400 hover:text-red-600"
                        onClick={() => confirm({ title: 'Delete User', message: `Delete "${u.email}"? This action cannot be undone.`, onConfirm: () => deleteMut.mutate(u.id) })}>
                        <i className="ti ti-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {/* New user */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="New User">
        <form onSubmit={handleSubmit(d => createMut.mutate(d))} className="space-y-4">
          <Input label="Email" type="email" placeholder="user@example.com" error={errors.email?.message} {...register('email')} />
          <Input label="Password" type="password" placeholder="At least 6 characters" error={errors.password?.message} {...register('password')} />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Role</label>
            <select className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" {...register('roleName')}>
              {roles?.map(r => <option key={r.id} value={r.name}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>)}
            </select>
            {errors.roleName && <p className="text-xs text-red-500">{errors.roleName.message}</p>}
          </div>
          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <Button type="submit" loading={createMut.isPending} disabled={createMut.isPending}>Create</Button>
            <Button type="button" variant="secondary" onClick={() => setCreateModal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* Change role */}
      <Modal open={Boolean(roleModal)} onClose={() => setRoleModal(null)} title="Change Role">
        {roleModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Change the role for <span className="font-medium text-gray-800">{roleModal.email}</span>.
            </p>
            <div className="space-y-2">
              {roles?.map(r => (
                <label key={r.id} className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${selectedRole === r.name ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedRole === r.name ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                    {selectedRole === r.name && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <input type="radio" className="sr-only" checked={selectedRole === r.name} onChange={() => setSelectedRole(r.name)} />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{r.name}</p>
                    {r.description && <p className="text-xs text-gray-400">{r.description}</p>}
                  </div>
                  <div className="ml-auto"><Badge color={ROLE_COLORS[r.name] ?? 'gray'}>{r.name}</Badge></div>
                </label>
              ))}
            </div>
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <Button onClick={() => roleMut.mutate({ id: roleModal.id, roleName: selectedRole })}
                loading={roleMut.isPending} disabled={roleMut.isPending || selectedRole === roleModal.roleName}>
                Update Role
              </Button>
              <Button variant="secondary" onClick={() => setRoleModal(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
